/* ============================================================
   HORCERY — Editorial: pinned, gated step-through.
   Engages when the section crosses screen-centre (a window a fast
   scroll can't skip), snaps into place, then FREEZES page scroll.
   Wheel/touch delta is accumulated; once it passes a per-step
   threshold we advance exactly ONE line, then lock for a dwell so
   each line holds. Only opacity/blur cross-fades — text never moves.
   At the first / last line the section HOLDS and needs one more
   deliberate gesture to unpin (an extra "notch" at each end).
   Desktop step distance comes from the "Scroll length per message"
   setting; touch/mobile keeps a shorter fixed threshold.
   No-JS / reduced-motion: messages stay stacked and readable.
   ============================================================ */
(function () {
  'use strict';

  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var TOUCH_STEP = 46;   // mobile: shorter swipe threshold (left as-is)
  var DWELL = 380;       // ms lock after a step (a line holds before the next)
  var ENGAGE_LOCK = 260; // ms lock while the entry snap settles

  function initEditorial(section) {
    if (!section || section.__hzInit) return;
    var msgs = Array.prototype.slice.call(section.querySelectorAll('[data-hz-msg]'));
    if (msgs.length < 2) return;
    if (reduce) return;                       // reduced motion → stacked, no jack
    return;                                    // Horcery: pin/scroll-jack DISABLED — renders as a normal static text section (no page-jump)
    section.__hzInit = true;
    section.setAttribute('data-hz-scroll', '');

    // Desktop step distance derives from the "Scroll length per message" setting
    // (60–160), scaled to the viewport so bigger screens ask for more scroll.
    var holdVh = parseInt(section.style.getPropertyValue('--hz-hold'), 10) || 120;
    var idx = 0, engaged = false, locked = false, accum = 0, touchY = null;

    function vh() { return window.innerHeight; }
    function wheelStep() { return Math.max(90, holdVh / 100 * vh() * 0.18); }

    function setActive(i) {
      i = Math.min(Math.max(i, 0), msgs.length - 1);
      idx = i;
      for (var k = 0; k < msgs.length; k++) {
        msgs[k].classList.remove('is-active', 'is-past', 'is-future');
        msgs[k].classList.add(k === i ? 'is-active' : (k < i ? 'is-past' : 'is-future'));
      }
    }
    setActive(0);

    function coversCentre() {
      var r = section.getBoundingClientRect();
      return r.top <= vh() * 0.5 && r.bottom >= vh() * 0.5;
    }
    function snapSmooth() {
      var r = section.getBoundingClientRect();
      if (Math.abs(r.top) > 2) window.scrollTo({ top: window.scrollY + r.top, behavior: 'smooth' });
    }
    function alignInstant() {
      var r = section.getBoundingClientRect();
      if (Math.abs(r.top) > 1) window.scrollTo(0, window.scrollY + r.top);
    }
    function atEnd(dir) { return (dir > 0 && idx >= msgs.length - 1) || (dir < 0 && idx <= 0); }

    // Returns true when the gesture is consumed (page must stay frozen).
    function handle(delta, stepThreshold) {
      if (!coversCentre()) { engaged = false; return false; }   // not our section → normal scroll
      var dir = delta > 0 ? 1 : -1;

      // Already released at an end and still pushing outward → let the page scroll (do NOT re-pin).
      if (!engaged && atEnd(dir)) return false;

      if (!engaged) {                                           // pin + snap into place
        engaged = true; accum = 0;
        snapSmooth();
        locked = true;
        setTimeout(function () { locked = false; alignInstant(); }, ENGAGE_LOCK);
        return true;                                            // consume the engaging gesture (no step yet)
      }

      if (locked) { accum = 0; return true; }                  // absorb momentum during the dwell / snap

      if (atEnd(dir)) {                                         // extra held notch at each end
        accum += delta;
        if (Math.abs(accum) >= stepThreshold) {                // one more deliberate gesture → leave
          accum = 0; engaged = false;
          return false;                                        // release THIS gesture to the page
        }
        return true;                                           // still holding the end line
      }

      accum += delta;
      if (Math.abs(accum) >= stepThreshold) {                  // one gesture-worth of scroll = one step
        accum = 0;
        setActive(idx + dir);
        locked = true;
        setTimeout(function () { locked = false; }, DWELL);
        alignInstant();                                        // keep it pinned
      }
      return true;                                             // freeze the page
    }

    // --- Named listeners so they can be removed on re-init / unload ---
    function onWheel(e) { if (handle(e.deltaY, wheelStep())) e.preventDefault(); }
    function onTouchStart(e) { touchY = e.touches[0].clientY; }
    function onTouchMove(e) {
      if (touchY === null) return;
      var dy = touchY - e.touches[0].clientY;          // >0 = swipe up = forward
      touchY = e.touches[0].clientY;
      if (Math.abs(dy) < 2) return;
      if (handle(dy * 2.2, TOUCH_STEP)) e.preventDefault();    // touch deltas are small — amplify
    }
    function onTouchEnd() { touchY = null; }
    function onKey(e) {
      if (!coversCentre()) return;
      if (e.key === 'ArrowDown' || e.key === 'PageDown') { if (handle(wheelStep(), wheelStep())) e.preventDefault(); }
      else if (e.key === 'ArrowUp' || e.key === 'PageUp') { if (handle(-wheelStep(), wheelStep())) e.preventDefault(); }
    }

    window.addEventListener('wheel', onWheel, { passive: false });
    window.addEventListener('touchstart', onTouchStart, { passive: true });
    window.addEventListener('touchmove', onTouchMove, { passive: false });
    window.addEventListener('touchend', onTouchEnd, { passive: true });
    section.setAttribute('tabindex', '-1');
    window.addEventListener('keydown', onKey);

    section.__hzCleanup = function () {
      window.removeEventListener('wheel', onWheel);
      window.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onTouchEnd);
      window.removeEventListener('keydown', onKey);
    };
  }

  function teardown(el) {
    if (!el) return;
    if (el.__hzCleanup) { el.__hzCleanup(); el.__hzCleanup = null; }
    el.__hzInit = false;
    el.removeAttribute('data-hz-scroll');
  }

  function initAll() { document.querySelectorAll('[data-hz-editorial]').forEach(initEditorial); }
  if (document.readyState !== 'loading') initAll();
  else document.addEventListener('DOMContentLoaded', initAll);

  document.addEventListener('shopify:section:load', function (e) {
    var el = e.target.querySelector('[data-hz-editorial]');
    if (el) { teardown(el); initEditorial(el); }
  });
  document.addEventListener('shopify:section:unload', function (e) {
    var el = e.target.querySelector('[data-hz-editorial]');
    if (el) teardown(el);
  });
})();
